import { useEffect, useRef } from 'react';

/**
 * Focus-trap + Escape-to-close hook for modal dialogs, ported from the
 * creator-portal prototype (ROU-1192). Moves focus into the modal on open,
 * cycles Tab within it, and restores focus to the prior element on close.
 *
 * When the modal has no focusable children, focus falls back to the container
 * itself; the hook sets `tabIndex = -1` on it so the fallback actually takes,
 * so callers don't have to remember to.
 */

const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

// Skip elements that aren't rendered (display:none / visibility:hidden), so Tab
// never lands on an invisible control the selector would otherwise match.
const isVisible = (element: HTMLElement): boolean => element.getClientRects().length > 0;

const getFocusableElements = (modal: HTMLElement): HTMLElement[] =>
    [...modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(isVisible);

// A bare container isn't focusable by default; make it programmatically focusable
// so focusing it as the last-resort fallback is not a silent no-op.
const ensureContainerFocusable = (modal: HTMLElement): void => {
    if (!modal.hasAttribute('tabindex')) {
        modal.tabIndex = -1;
    }
};

export const useModalFocus = <TElement extends HTMLElement = HTMLElement>(
    isOpen: boolean,
    onClose: () => void,
    focusKey: unknown = null
) => {
    const modalRef = useRef<TElement>(null);
    const closeHandlerRef = useRef(onClose);
    closeHandlerRef.current = onClose;

    useEffect(() => {
        if (!isOpen || !modalRef.current) {
            return undefined;
        }

        const modal = modalRef.current;
        const previousFocus = document.activeElement;
        const focusableElements = getFocusableElements(modal);
        const initialFocus = modal.querySelector<HTMLElement>('[data-autofocus]') ?? focusableElements[0] ?? modal;
        if (initialFocus === modal) {
            ensureContainerFocusable(modal);
        }
        initialFocus.focus();

        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeHandlerRef.current();
                return;
            }

            if (event.key !== 'Tab') {
                return;
            }
            const currentFocusableElements = getFocusableElements(modal);
            if (!currentFocusableElements.length) {
                event.preventDefault();
                ensureContainerFocusable(modal);
                modal.focus();
                return;
            }

            const firstElement = currentFocusableElements[0];
            const lastElement = currentFocusableElements[currentFocusableElements.length - 1];
            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (previousFocus instanceof HTMLElement) {
                previousFocus.focus();
            }
        };
    }, [isOpen, focusKey]);

    return modalRef;
};
